import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LangProvider } from "@/context/LangContext";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import LandingPage from "@/pages/LandingPage";
import Dashboard from "@/pages/Dashboard";
import AdminPage from "@/pages/AdminPage";
import ReportBuilder from "@/pages/ReportBuilder";
import ReportView from "@/pages/ReportView";
import ReportComparer from "@/pages/ReportComparer";
import SamplePage from "@/pages/SamplePage";
import FoundersPage from "@/pages/FoundersPage";
import LegalPage from "@/pages/LegalPage";
import BillingPage from "@/pages/BillingPage";
import NewsPage from "@/pages/NewsPage";
import AdvisorsGallery from "@/pages/AdvisorsGallery";
import AdvisorProfile from "@/pages/AdvisorProfile";
import CreateAdvisorProfile from "@/pages/CreateAdvisorProfile";
import useVisitTracker from "@/hooks/useVisitTracker";
import LinkedInRouteTracker from "@/components/LinkedInRouteTracker";

const TrackedRoutes = ({ children }) => {
  useVisitTracker();
  return children;
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#070709]">
      <div className="w-8 h-8 border-2 border-[#01AF9B] border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
};

function LegacyAdvisorsRedirect() {
  const { pathname, search, hash } = useLocation();
  return <Navigate to={pathname.replace(/^\/advisors/, "/professionals") + search + hash} replace />;
}

function App() {
  return (
    <BrowserRouter>
      <LangProvider>
        <AuthProvider>
          <LinkedInRouteTracker />
          <TrackedRoutes>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/sample" element={<SamplePage />} />
              <Route path="/founders" element={<FoundersPage />} />
              {/* Legacy /compare URL → permanent redirect to new sample page */}
              <Route path="/compare" element={<Navigate to="/sample" replace />} />
              <Route path="/terms" element={<LegalPage kind="terms" />} />
              <Route path="/privacy" element={<LegalPage kind="privacy" />} />
              <Route path="/refund" element={<LegalPage kind="refund" />} />
              <Route path="/billing" element={<BillingPage />} />
              <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
              <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
              <Route path="/subscribe" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
              <Route path="/reset-password" element={<PublicRoute><ResetPasswordPage /></PublicRoute>} />
              <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
              <Route path="/report/new" element={<ProtectedRoute><ReportBuilder /></ProtectedRoute>} />
              <Route path="/report/:id" element={<ProtectedRoute><ReportView /></ProtectedRoute>} />
              <Route path="/report/:id/compare" element={<ProtectedRoute><ReportComparer /></ProtectedRoute>} />
              <Route path="/report/:id/edit" element={<ProtectedRoute><ReportBuilder /></ProtectedRoute>} />
              <Route path="/news" element={<ProtectedRoute><NewsPage /></ProtectedRoute>} />
              <Route path="/professionals" element={<ProtectedRoute><AdvisorsGallery /></ProtectedRoute>} />
              <Route path="/professionals/create" element={<ProtectedRoute><CreateAdvisorProfile /></ProtectedRoute>} />
              <Route path="/professionals/edit" element={<ProtectedRoute><CreateAdvisorProfile editMode /></ProtectedRoute>} />
              <Route path="/professionals/:slug" element={<ProtectedRoute><AdvisorProfile /></ProtectedRoute>} />
              <Route path="/advisors/*" element={<LegacyAdvisorsRedirect />} />
              <Route path="/advisors" element={<LegacyAdvisorsRedirect />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </TrackedRoutes>
        </AuthProvider>
      </LangProvider>
    </BrowserRouter>
  );
}

export default App;
